import { useState, useEffect } from "react";
import { TabCompletionHeader } from "./common/TabCompletionHeader";
import { HeartHandshake, Eye, Landmark, FileCheck } from "lucide-react";
import { useWizard } from "@/contexts/WizardContext";
import { MetricCard } from "./common/MetricCard";
import { EthicsCard } from "./governance/EthicsCard";
import { TransparencyCard } from "./governance/TransparencyCard";
import { LeadershipCard } from "./governance/LeadershipCard";
import { AccountabilityCard } from "./governance/AccountabilityCard";
import { WizardSectionNav } from "@/components/wizard/WizardSectionNav";
import { getSectionByCardKey } from "@shared/section-definitions";

export function GovernanceTab() {
  const [expandedMetric, setExpandedMetric] = useState<string | null>(null);
  const {
    ethicsCompletion,
    transparencyCompletion,
    leadershipCompletion,
    accountabilityCompletion,
    governanceOverallCompletion,
    isLocked,
    currentSectionId,
    currentSection,
    goToSection,
  } = useWizard();

  // Sync expandedMetric with currentSection
  useEffect(() => {
    if (currentSection && currentSection.tab === "governance") {
      setExpandedMetric(currentSection.cardKey);
    }
  }, [currentSection]);

  const handleSectionExpand = (cardKey: string) => {
    setExpandedMetric(cardKey);
    const section = getSectionByCardKey("governance", cardKey);
    if (section) {
      goToSection(section.id);
    }
  };

  const handleSectionClose = () => {
    setExpandedMetric(null);
  };

  const sectionCompletions = {
    ethics: ethicsCompletion,
    transparency: transparencyCompletion,
    leadership: leadershipCompletion,
    accountability: accountabilityCompletion,
  };

  return (
    <div className="flex gap-6">
      {/* Left Sidebar Navigation */}
      <div className="w-64 flex-shrink-0 hidden lg:block sticky top-[8.25rem] self-start h-[calc(100vh-16.5rem)]">
        <WizardSectionNav
          currentTab="governance"
          currentSectionId={currentSectionId}
          onSectionClick={(section) => handleSectionExpand(section.cardKey)}
          sectionCompletions={sectionCompletions}
        />
      </div>

      {/* Main Content */}
      <div className="flex-1 space-y-4">
        <div>
          <TabCompletionHeader
            title="Governance"
            completion={governanceOverallCompletion}
            statusLabel={isLocked ? "Section finalized" : "Section in progress"}
          />
        </div>

        {expandedMetric === "ethics" ? (
          <EthicsCard onCollapse={handleSectionClose} />
        ) : expandedMetric === "transparency" ? (
          <TransparencyCard onCollapse={handleSectionClose} />
        ) : expandedMetric === "leadership" ? (
          <LeadershipCard onCollapse={handleSectionClose} />
        ) : expandedMetric === "accountability" ? (
          <AccountabilityCard onCollapse={handleSectionClose} />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <MetricCard
              icon={HeartHandshake}
              title="Ethics & Integrity"
              description="Anti-corruption policies, whistleblower protections, and ethical conduct standards"
              completion={ethicsCompletion}
              onClick={() => handleSectionExpand("ethics")}
            />
            <MetricCard
              icon={Eye}
              title="Transparency & Disclosure"
              description="Disclosure practices, audit processes, and stakeholder reporting"
              completion={transparencyCompletion}
              onClick={() => handleSectionExpand("transparency")}
            />
            <MetricCard
              icon={Landmark}
              title="Leadership & Governance"
              description="Board composition, executive oversight, and governance structure"
              completion={leadershipCompletion}
              onClick={() => handleSectionExpand("leadership")}
            />
            <MetricCard
              icon={FileCheck}
              title="Accountability & Reporting"
              description="Performance metrics, compliance frameworks, and accountability mechanisms"
              completion={accountabilityCompletion}
              onClick={() => handleSectionExpand("accountability")}
            />
          </div>
        )}
      </div>
    </div>
  );
}
